import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Chip, Divider } from "@mui/material";
import dayjs from "dayjs";
import { CorporateActionRow } from "@/models/stock-corporate-actions";

interface CorporateActionDetailsModalProps {
    row: CorporateActionRow | null;
    onClose: () => void;
}

const FAMILY_LABELS: Record<string, string> = {
    splits: "Splits",
    dividends: "Dividends",
    deals: "Deals & Spin-offs",
    buybacks: "Buybacks",
    equity_offerings: "Equity Offerings",
    lockups: "Lock-ups",
    indexes: "Index Changes",
};

// Display labels for the typed per-family `details` keys, in render order.
const DETAIL_LABELS: Record<string, string> = {
    ratio: "Ratio",
    direction: "Direction",
    rate: "Rate",
    splitType: "Split Type",
    applied: "Applied to Price History",
    amount: "Amount",
    netAmount: "Net Amount",
    grossAmount: "Gross Amount",
    currency: "Currency",
    frequency: "Frequency",
    dividendType: "Dividend Type",
    supplementalType: "Supplemental Type",
    special: "Special Dividend",
    roleType: "Role",
    indexName: "Index",
    action: "Action",
    announcedDate: "Announced Date",
    exDate: "Ex-Date",
    recordDate: "Record Date",
    payDate: "Pay Date",
    effectiveDate: "Effective Date",
    situation: "Situation",
};

// Internal plumbing keys that carry no meaning for the user.
const HIDDEN_DETAIL_KEYS = new Set(["externalId", "roleTypeId", "source", "marketCap", "hasTranscripts", "hasCallDetails"]);

const DATE_DETAIL_KEYS = new Set(["exDate", "recordDate", "payDate", "announcedDate", "effectiveDate"]);

const formatDetailLabel = (key: string) => DETAIL_LABELS[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

const formatDetailValue = (key: string, value: unknown): string => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (DATE_DETAIL_KEYS.has(key)) {
        const parsed = dayjs(String(value));
        if (parsed.isValid()) return parsed.format("D MMM YYYY");
    }
    return String(value);
};

/** Sort the details keys into the curated DETAIL_LABELS order, unknown keys last. */
const orderedDetailEntries = (details: Record<string, unknown>) => {
    const order = Object.keys(DETAIL_LABELS);
    return Object.entries(details)
        .filter(([key, value]) => !HIDDEN_DETAIL_KEYS.has(key) && value !== null && value !== undefined && value !== "")
        .sort(([a], [b]) => {
            const ai = order.indexOf(a);
            const bi = order.indexOf(b);
            if (ai === -1 && bi === -1) return a.localeCompare(b);
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        });
};

/** Dialog rendering the structured per-family `details` payload of a corporate-action row. */
const CorporateActionDetailsModal: React.FC<CorporateActionDetailsModalProps> = ({ row, onClose }) => {
    const detailEntries = row?.details ? orderedDetailEntries(row.details) : [];

    return (
        <Dialog open={!!row} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6">Corporate Action Details</Typography>
            </DialogTitle>
            <DialogContent>
                {row && (
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            {row.title || row.type}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                            <Chip size="small" variant="outlined" color="primary" label={FAMILY_LABELS[row.family] || row.family} />
                            {row.type && <Chip size="small" variant="outlined" label={row.type} />}
                            {row.ticker && <Chip size="small" variant="outlined" label={row.ticker} />}
                            {!!row.date && <Chip size="small" variant="outlined" label={dayjs.utc(row.date).format("D MMM YYYY")} />}
                        </Box>
                        {detailEntries.length > 0 && (
                            <>
                                <Divider sx={{ mb: 2 }} />
                                {detailEntries.map(([key, value]) => (
                                    <Box key={key} sx={{ mb: 1 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "text.secondary",
                                            }}
                                        >
                                            <strong>{formatDetailLabel(key)}:</strong> {formatDetailValue(key, value)}
                                        </Typography>
                                    </Box>
                                ))}
                            </>
                        )}
                        {detailEntries.length === 0 && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                }}
                            >
                                No further details available for this action.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CorporateActionDetailsModal;
